import React, { Suspense, createContext, useContext, useRef } from 'react';

import UFOSegment from '@atlaskit/react-ufo/segment';

import {
	getDataFetcherForSuspense,
	type SuspenseableDataFetcher,
} from '@confluence/query-preloader-boundary/entry-points/getDataFetcherForSuspense.ts';
import { HydrationSafeSuspense } from '@confluence/hydration-utils/entry-points/HydrationSafeSuspense.tsx';
import { expVal } from '@confluence/feature-experiments';

// Enhanced context to track the full boundary chain from root to current
const SuspenseBoundaryNameContext = createContext<string[]>([]);

// Returns the full boundary chain from root to current
export const useSuspenseBoundaryName = (): string[] => {
	return useContext(SuspenseBoundaryNameContext);
};

function SuspenseWithDataFetcherReader({
	dataFetcher,
	children,
}: {
	dataFetcher: SuspenseableDataFetcher | null;
	children: React.ReactNode;
}) {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		dataFetcher?.read();
	}
	return <>{children}</>;
}

export function SuspenseWithDataFetcher({
	dataFetcherName,
	fallback = null,
	children,
}: {
	dataFetcherName: string;
	fallback?: React.ReactNode;
	children: React.ReactNode;
}) {
	// Capture the SSR streaming value on initial render using useRef
	// This ensures the component tree doesn't change when the global variable
	// is deleted during route navigation
	const isSSRStreaming = useRef(window.__SSR_REACT_STREAMING__);

	// Get the current boundary chain from parent context
	const parentBoundaryChain = useContext(SuspenseBoundaryNameContext);

	// Create new chain by appending current boundary to parent chain
	const currentBoundaryChain = [...parentBoundaryChain, dataFetcherName];

	if (isSSRStreaming.current) {
		const dataFetcher = getDataFetcherForSuspense(dataFetcherName, isSSRStreaming.current);

		// If confluence_frontend_use_hydration_safe_suspense is on,
		// use a suspense boundary that will block updates (via memo) until hydration has completed
		const SuspenseComponent = expVal(
			'confluence_frontend_use_hydration_safe_suspense',
			'isEnabled',
			false,
		)
			? HydrationSafeSuspense
			: Suspense;

		return (
			<SuspenseBoundaryNameContext.Provider value={currentBoundaryChain}>
				<SuspenseComponent fallback={fallback}>
					<SuspenseWithDataFetcherReader dataFetcher={dataFetcher}>
						<UFOSegment name={`stream-suspense-${dataFetcherName}`}>{children}</UFOSegment>
					</SuspenseWithDataFetcherReader>
				</SuspenseComponent>
			</SuspenseBoundaryNameContext.Provider>
		);
	} else {
		return <>{children}</>;
	}
}
